import { ReactNode, useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AccountCircle } from '@/assets/icons';
import { cn } from '@/lib/utils';
import { useConfigStore, configSelectors } from '@/lib/config';
import { DynamicNav } from '@/components/shared/DynamicNav';
import { AppointmentRefreshTimer } from '@/components/shared/AppointmentRefreshTimer';
import { useDepartmentTheme } from '@/components/providers/DepartmentThemeProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/stores/authStore';
import { useScale } from '@/lib/hooks/useScale';
import PranikLogo from '@/assets/logo.svg';
import { LabReportsModal } from '@/components/consultation/LabReportsModal';

export interface PatientData {
  name: string;
  identifier: string;
  age?: number;
  gender?: string;
  birthDate?: string;
}

interface AppLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  patientData?: PatientData | null;
  /** Patient FHIR ID — enables lab report upload in consultation header */
  patientId?: string;
  /** Encounter FHIR ID — links uploaded lab reports to the encounter */
  encounterId?: string;
  /** Appointment FHIR ID — links uploaded lab reports to the appointment */
  appointmentId?: string;
}

export function AppLayout({
  children,
  showHeader = true,
  patientData,
  patientId,
  encounterId,
  appointmentId,
}: AppLayoutProps) {
  const location = useLocation();
  const config = useConfigStore(configSelectors.config);
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const queryClient = useQueryClient();
  const { isAyurveda, appName } = useDepartmentTheme();
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const scale = useScale();

  const isConsultationRoute = location.pathname.startsWith('/consultation/');
  const showPatientDetailsInHeader = isConsultationRoute && patientData;
  const showAppointmentRefreshTimer =
    location.pathname === '/dashboard' && projectConfig?.services.showSessionTimer === true;
  const refreshAppointments = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
      type: 'active',
    });
  }, [queryClient]);

  return (
    <div
      className={cn(
        'flex h-dvh flex-col overflow-hidden',
        isConsultationRoute && isAyurveda ? 'consultation-bg' : 'bg-background'
      )}
    >
      {showHeader && (
        <header className="flex-shrink-0">
          <div
            className={cn(
              'flex items-center justify-between',
              isConsultationRoute ? 'w-full px-10' : 'container'
            )}
            style={{ height: 'calc(64px * var(--app-scale))' }}
          >
            <div className="flex items-center" style={{ gap: 'calc(24px * var(--app-scale))' }}>
              {config.logo?.enabled ? (
                <Link to="/dashboard" className="flex items-center">
                  <img
                    src={PranikLogo}
                    alt={config.logo.alt || 'Logo'}
                    style={{
                      height: config.logo.height
                        ? `calc(${config.logo.height}px * var(--app-scale))`
                        : 'calc(32px * var(--app-scale))',
                      width: config.logo.width
                        ? `calc(${config.logo.width}px * var(--app-scale))`
                        : 'auto',
                    }}
                    className="object-contain"
                  />
                </Link>
              ) : (
                <Link
                  to="/dashboard"
                  className={`font-serif text-[calc(24px*var(--app-scale))] font-semibold transition-colors ${
                    isAyurveda
                      ? 'text-[#2E7D32] hover:text-[#1B5E20]'
                      : 'text-pranik-brand hover:text-pranik-brand-dark'
                  }`}
                >
                  {isAyurveda ? appName : config.appName || 'Pranik.AI'}
                </Link>
              )}
            </div>

            <div className="flex items-center" style={{ gap: 'calc(12px * var(--app-scale))' }}>
              {showAppointmentRefreshTimer && (
                <AppointmentRefreshTimer onRefresh={refreshAppointments} />
              )}
              {/* Lab Reports button — shown on consultation routes when patientId is available */}
              {isConsultationRoute && patientId && (
                <LabReportsModal
                  patientId={patientId}
                  encounterId={encounterId}
                  appointmentId={appointmentId}
                />
              )}
              {showPatientDetailsInHeader ? (
                <div className="relative flex-shrink-0">
                  <button
                    className="flex items-center gap-1 rounded-2xl bg-[#f8f9fb] transition-colors hover:bg-[#e8e9eb]"
                    style={{
                      paddingLeft: 'calc(16px * var(--app-scale))',
                      paddingRight: 'calc(16px * var(--app-scale))',
                      paddingTop: 'calc(6px * var(--app-scale))',
                      paddingBottom: 'calc(6px * var(--app-scale))',
                    }}
                    onMouseEnter={() => setShowPatientDetails(true)}
                    onMouseLeave={() => setShowPatientDetails(false)}
                  >
                    <span
                      className="font-hanken font-medium tracking-[0px] text-[#586695]"
                      style={{
                        fontSize: 'calc(13px * var(--app-scale))',
                        lineHeight: 'calc(20px * var(--app-scale))',
                      }}
                    >
                      Patient Details
                    </span>
                    <div className="flex items-center justify-center rounded-full">
                      <AccountCircle size={24 * scale} fill="#2E3E6F" />
                    </div>
                  </button>

                  {showPatientDetails && patientData && (
                    <div
                      className="absolute right-0 top-full z-50 rounded-xl border border-gray-200 bg-white shadow-lg"
                      style={{
                        marginTop: 'calc(8px * var(--app-scale))',
                        minWidth: 'calc(280px * var(--app-scale))',
                        padding: 'calc(16px * var(--app-scale))',
                      }}
                    >
                      <div className="space-y-2">
                        <div className="border-b border-gray-200 pb-2">
                          <p
                            className="font-semibold text-gray-700"
                            style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                          >
                            Patient Information
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between">
                            <span
                              className="text-gray-600"
                              style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                            >
                              Name:
                            </span>
                            <span
                              className="font-medium text-gray-900"
                              style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                            >
                              {patientData.name}
                            </span>
                          </div>
                          {patientData.age && (
                            <div className="flex justify-between">
                              <span
                                className="text-gray-600"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                Age:
                              </span>
                              <span
                                className="font-medium text-gray-900"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                {patientData.age} years
                              </span>
                            </div>
                          )}
                          {patientData.gender && (
                            <div className="flex justify-between">
                              <span
                                className="text-gray-600"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                Gender:
                              </span>
                              <span
                                className="font-medium capitalize text-gray-900"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                {patientData.gender}
                              </span>
                            </div>
                          )}
                          {patientData.birthDate && (
                            <div className="flex justify-between">
                              <span
                                className="text-gray-600"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                Birth Date:
                              </span>
                              <span
                                className="font-medium text-gray-900"
                                style={{ fontSize: 'calc(13px * var(--app-scale))' }}
                              >
                                {patientData.birthDate}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <DynamicNav />
              )}
            </div>
          </div>
        </header>
      )}

      <main
        className={cn(
          'scrollbar-thin flex min-h-0 flex-1 flex-col',
          isConsultationRoute ? 'overflow-hidden' : 'overflow-y-auto'
        )}
      >
        <div
          className={cn('flex flex-col', isConsultationRoute ? 'h-full w-full px-6' : 'container')}
        >
          {children}
        </div>
      </main>

      {/* Footer — consultation pages already render their own "Powered by
          Pranik.AI" line inside the middle column (matching Figma), so skip
          this global one there to avoid showing it twice. */}
      {!isConsultationRoute && (
        <footer
          className="flex-shrink-0 text-center"
          style={{
            paddingTop: 'calc(8px * var(--app-scale))',
            paddingBottom: 'calc(8px * var(--app-scale))',
          }}
        >
          <p
            className="font-hanken font-bold tracking-[-0.16px] text-[#4d68b9]"
            style={{
              fontSize: 'calc(16px * var(--app-scale))',
              lineHeight: 'calc(22px * var(--app-scale))',
            }}
          >
            Powered by Pranik.AI
          </p>
        </footer>
      )}
    </div>
  );
}
